import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, User as UserIcon, ShieldAlert, LogOut } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { PortalPopover } from '../../ui/PortalPopover';
import { Button } from '../../ui/Button';

export const UserNavMenu: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <div className="relative">
      <Button
        ref={userMenuButtonRef as any}
        type="button"
        variant="ghost"
        size="xs"
        onClick={() => setUserMenuOpen(!userMenuOpen)}
        className="flex items-center gap-1.5 px-2 py-1 rounded hover:bg-cf-surfaceActive text-xs text-cf-textPrimary transition-colors focus:outline-none cursor-pointer h-auto"
        title="Account settings"
      >
        <div className="w-5 h-5 rounded-full bg-cf-surfaceActive border border-cf-border flex items-center justify-center text-cf-orange font-semibold text-xs">
          {(user?.full_name || user?.email || 'U')[0].toUpperCase()}
        </div>
        <span className="font-medium text-xs max-w-[110px] truncate hidden md:inline">
          {user?.full_name || user?.email?.split('@')[0] || 'My Account'}
        </span>
        <ChevronDown size={11} className="text-cf-textSecondary" />
      </Button>

      <PortalPopover
        isOpen={userMenuOpen}
        onClose={() => setUserMenuOpen(false)}
        triggerRef={userMenuButtonRef}
        align="right"
        className="w-60 bg-cf-surface border border-cf-border rounded-lg shadow-xl overflow-hidden text-xs"
      >
        {/* User Info Header */}
        <div className="px-3 py-2 border-b border-cf-border bg-cf-bg/50">
          <div className="font-medium text-cf-textPrimary truncate text-xs">
            {user?.full_name || 'My Account'}
          </div>
          <div className="text-xs text-cf-textSecondary truncate">{user?.email}</div>
        </div>

        {/* Menu Items */}
        <div>
          <Button
            type="button"
            variant="ghost"
            fullWidth
            onClick={() => {
              setUserMenuOpen(false);
              navigate('/account');
            }}
            leftIcon={<UserIcon size={14} className="text-cf-orange" />}
            className="w-full justify-start gap-2 px-3 py-2 text-cf-textPrimary hover:bg-cf-surfaceActive transition-colors cursor-pointer h-auto rounded-none"
          >
            <span>Account settings</span>
          </Button>

          {user?.is_superadmin && (
            <Button
              type="button"
              variant="ghost"
              fullWidth
              onClick={() => {
                setUserMenuOpen(false);
                navigate('/admin');
              }}
              leftIcon={<ShieldAlert size={14} className="text-rose-600 dark:text-rose-400" />}
              className="w-full justify-start gap-2 px-3 py-2 text-rose-600 dark:text-rose-400 hover:bg-rose-500/10 transition-colors font-medium cursor-pointer h-auto rounded-none"
            >
              <span>Platform Super-Admin</span>
            </Button>
          )}
        </div>

        {/* Divider */}
        <div className="border-t border-cf-border" />

        {/* Sign Out */}
        <Button
          type="button"
          variant="ghost"
          fullWidth
          onClick={async () => {
            setUserMenuOpen(false);
            await logout();
            navigate('/login');
          }}
          leftIcon={<LogOut size={14} />}
          className="w-full justify-start gap-2 px-3 py-2 text-rose-600 dark:text-rose-400 hover:bg-rose-500/10 transition-colors font-medium cursor-pointer h-auto rounded-none"
        >
          <span>Sign Out</span>
        </Button>
      </PortalPopover>
    </div>
  );
};
